import { useEffect, useState } from "react";
import Login from "./Components/Static/Login/Login";
import HandshakeIcon from "@mui/icons-material/Handshake";
// import ControlPointIcon from "@mui/icons-material/ControlPoint"; // icono del menú "Añadir" (oculto)
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import InsightsIcon from '@mui/icons-material/Insights';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";

import {
  Switch,
  Route,
  Redirect,
  useHistory,
  useLocation,
} from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import Header from "./Components/Shared/Header/Header";
import { useDispatch, useSelector } from "react-redux";
import { useIsAuthenticated, useMsal, MsalProvider } from "@azure/msal-react";
import { BrowserAuthError, InteractionRequiredAuthError, IPublicClientApplication } from "@azure/msal-browser";
import Button from "@mui/material/Button";
import { loginRequest } from "./authConfig";
import { CustomNavigationClient } from "./Utils/NavigationClient";
import { loginAzure } from "./Service/AuthService/AuthService";
import { userLogin, userLogout } from "./Redux/User/UserAction";
import { MANAGE_USERS, validatePermission } from "./Utils/permissionsUtils";
import { installSessionInterceptor } from "./Utils/sessionInterceptor";
import { apiErrorMessage } from "./Utils/apiErrors";
import {
  addLoginAttempt,
  clearStaleMsalState,
  getLoginAttempts,
  MAX_LOGIN_ATTEMPTS,
  resetLoginAttempts,
  restartLogin,
} from "./Utils/authRecovery";
import Users from "./Pages/Users/Users";
import AedasLoading from "./Components/Shared/AedasLoading/AedasLoading";
import InventoryIcon from "@mui/icons-material/Inventory";
import Partner from "./Components/Static/Partner/Partner";
import Inventory from "./Components/Static/Iventory/inventory";
import { Certificates } from "./Pages/Certificates/Certificates";
import { Domains } from "./Pages/Domains/Domains";
import { NewItem } from "./Pages/NewItem/NewItem";
// Si la pantalla de carga dura más que esto, se ofrece volver a iniciar sesión
const SLOW_LOGIN_MS = 20000;

type AppProps = {
  pca: IPublicClientApplication;
};
function App({ pca }: AppProps) {
  const history = useHistory();
  const navigationClient = new CustomNavigationClient(history);
  pca.setNavigationClient(navigationClient);

  return (
    <>
      <ToastContainer autoClose={1500}></ToastContainer>
      <MsalProvider instance={pca}>
        <Router></Router>
      </MsalProvider>
    </>
  );
}

export default App;

const Router = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const history = useHistory();
  const isLogin = useSelector((state: any) => state.userReducer.isLogin);
  const canManageUsers = useSelector((state: any) =>
    validatePermission(state.userReducer.user?.permissions, MANAGE_USERS)
  );

  const isAuthenticated = useIsAuthenticated();
  const location = useLocation();
  const { instance, accounts, inProgress } = useMsal();
  const dispatch = useDispatch();

  // Motivo por el que no se ha podido entrar; con él, en vez de la pantalla
  // de carga se muestra el aviso con el botón para volver a iniciar sesión
  const [authError, setAuthError] = useState<string | null>(null);
  const [slowLogin, setSlowLogin] = useState(false);
  // Al cambiar se repite el login (p. ej. tras un 401 del back)
  const [loginKey, setLoginKey] = useState(0);

  useEffect(() => {
    if (isLogin) return;
    const timer = setTimeout(() => setSlowLogin(true), SLOW_LOGIN_MS);
    return () => clearTimeout(timer);
  }, [isLogin]);

  // 401 del back (token caducado o usuario desactivado): se descarta la sesión
  // y se repite el login. Si la cuenta ya no tiene acceso, sale el aviso.
  useEffect(() => {
    if (!isLogin) return;
    return installSessionInterceptor(() => {
      toast.error("Tu sesión ha caducado, vuelve a iniciar sesión");
      localStorage.removeItem("aedas_user");
      dispatch(userLogout());
      setIsLoading(true);
      setLoginKey((key) => key + 1);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLogin]);

  useEffect(() => {
    if (isLogin) {
      setIsLoading(false);
      history.push(location.pathname);
    }
  }, [isLogin]);

  // El back ha rechazado el acceso: se descarta la sesión guardada
  // ("aedas_user") y se muestra el aviso en vez de cargar para siempre
  const failLogin = (message: string) => {
    localStorage.removeItem("aedas_user");
    setAuthError(message);
    setIsLoading(false);
  };

  // Login por redirección a Microsoft, con tope de intentos por pestaña para
  // no entrar en bucle si Azure AD sigue fallando
  const redirectToLogin = (request: any, retryingStale = false) => {
    if (getLoginAttempts() >= MAX_LOGIN_ATTEMPTS) {
      setAuthError("No se ha podido iniciar sesión con tu cuenta de Microsoft después de varios intentos.");
      return;
    }
    addLoginAttempt();
    const redirect = request.account ? instance.acquireTokenRedirect(request) : instance.loginRedirect(request);
    redirect.catch((e: any) => {
      console.error(e);
      // Un login anterior quedó a medias y MSAL cree que sigue en curso
      // (inProgress ya es "none"): se limpia ese estado y se reintenta una vez
      if (!retryingStale && e instanceof BrowserAuthError && e.errorCode === "interaction_in_progress") {
        clearStaleMsalState();
        redirectToLogin(request, true);
        return;
      }
      setAuthError("No se ha podido abrir el inicio de sesión de Microsoft.");
    });
  };

  useEffect(() => {
    if (inProgress !== "none") return;
    if (!isAuthenticated) {
      redirectToLogin(loginRequest);
      return;
    }
    const request = {
      ...loginRequest,
      account: instance.getActiveAccount() ?? accounts[0],
    };
    instance
      .acquireTokenSilent(request)
      .then(async (response: any) => {
        let loginError: any = null;
        const responseBackend: any = await loginAzure(response.accessToken).catch((error: any) => {
          loginError = error;
        });
        if (responseBackend?.data?.token) {
          resetLoginAttempts();
          // Incluye token, employee y permissions
          localStorage.setItem("aedas_user", JSON.stringify(responseBackend.data));
          dispatch(userLogin(responseBackend.data, response.accessToken, responseBackend.data.employee?.azure_rol));
        } else {
          const username = response.account?.username;
          failLogin(
            apiErrorMessage(loginError, "No se ha podido comprobar tu acceso al inventario.") +
              (username ? ` (cuenta ${username})` : "")
          );
        }
      })
      .catch((e: any) => {
        console.log(e);
        // La cuenta guardada en el navegador ya no vale (sesión caducada,
        // contraseña cambiada...): hay que volver a pasar por Microsoft
        if (e instanceof InteractionRequiredAuthError) {
          redirectToLogin(request);
        } else {
          failLogin("No se ha podido recuperar tu sesión de Microsoft guardada en este navegador.");
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, inProgress, loginKey]);

  const menu: any = [
    {
      name: "Licencias",
      localPath: "/",
      icon: <InventoryIcon />,
      onClick: history.push,
      canView: true,
      shortName: "Licencias",
      hasPermissions: true,
    },
    {
      name: "Activos tecnológicos",
      localPath: "/activos-tecnologicos",
      icon: <InsightsIcon />,
      onClick: history.push,
      canView: true,
      shortName: "Act. Tec.",
      hasPermissions: true,
    },
    {
      name: "Dominios",
      localPath: "/dominios",
      icon: <TravelExploreIcon />,
      onClick: history.push,
      canView: true,
      shortName: "Dominios",
      hasPermissions: true,
    },
    {
      name: "Certificados",
      localPath: "/certificados",
      icon: <VerifiedUserIcon />,
      onClick: history.push,
      canView: true,
      shortName: "Cert.",
      hasPermissions: true,
    },
    // Oculto por ahora: se crea desde el botón "Añadir" de cada sección
    // {
      // name: "Añadir",
      // localPath: "/nuevo",
      // icon: <ControlPointIcon />,
      // onClick: history.push,
      // canView: true,
      // shortName: "Añadir",
      // hasPermissions: true,
    // },
    {
      name: "Partner / Fabricante",
      localPath: "/partner",
      icon: <HandshakeIcon />,
      onClick: history.push,
      canView: true,
      shortName: "Part. / Fabr.",
      hasPermissions: true,
    },
    {
      name: "Gestión de usuarios",
      localPath: "/usuarios",
      icon: <ManageAccountsIcon />,
      onClick: history.push,
      canView: canManageUsers,
      shortName: "Usuarios",
      hasPermissions: canManageUsers,
    },
  ];

  if (authError) {
    return (
      <div className="authMessage">
        <h2 className="section-title">No se ha podido iniciar sesión</h2>
        <p className="paragraph-text">
          {authError.replace(/\.$/, "")}. Pulsa el botón para cerrar la sesión guardada en este navegador y volver a entrar con tu
          cuenta de AEDAS.
        </p>
        <p className="paragraph-text">
          Si vuelve a aparecer este aviso con tu cuenta de AEDAS, tu usuario no está dado de alta en el inventario o
          no tiene permisos: avisa al equipo de soporte.
        </p>
        <Button variant="contained" onClick={() => restartLogin(instance)}>
          Volver a iniciar sesión
        </Button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="authMessage">
        <AedasLoading></AedasLoading>
        {slowLogin ? (
          <>
            <p className="paragraph-text">¿Tarda demasiado en entrar?</p>
            <Button variant="contained" onClick={() => restartLogin(instance)}>
              Volver a iniciar sesión
            </Button>
          </>
        ) : null}
      </div>
    );
  }

  return (
    <>
      <div className="d-flex" style={{ height: "100vh" }}>
        {isLogin ? (
          <Header menu={menu} open={isOpen} setIsOpen={setIsOpen}></Header>
        ) : (
          <></>
        )}

        {/* La página no hace scroll: cada sección ocupa el alto de la ventana
            y solo hacen scroll las tablas (o el formulario, si no cabe) */}
        <div
          style={
            isLogin ? { flex: 1, marginLeft: "70px" } : {}
          }
          className="w-100 appContent"
        >
          <Switch>
            <Route exact path="/login">
              <Login isLogin={isLogin}></Login>
            </Route>
            
            <PrivateRoute auth={isLogin} hasPermissions={true} exact path="/">
              <Inventory itemType={'licencia'}/>
            </PrivateRoute>
            

            <PrivateRoute auth={isLogin} hasPermissions={true} exact path="/activos-tecnologicos">
              <Inventory itemType={'activo'}/>
            </PrivateRoute>

            <PrivateRoute auth={isLogin} hasPermissions={true} exact path="/dominios">
              <Domains />
            </PrivateRoute>

            <PrivateRoute auth={isLogin} hasPermissions={true} exact path="/certificados">
              <Certificates />
            </PrivateRoute>


            <PrivateRoute auth={isLogin} hasPermissions={true} exact path="/nuevo">
              <NewItem />
            </PrivateRoute>

            {/* El tipo se toma de la URL: /nuevo/licencia, /nuevo/activo, /nuevo/dominio... */}
            <PrivateRoute auth={isLogin} hasPermissions={true} exact path="/nuevo/:tipo(licencia|activo|dominio|certificado|partner)">
              <NewItem />
            </PrivateRoute>

            <PrivateRoute auth={isLogin} hasPermissions={true} exact path="/editar/activo/:id">
              <NewItem edit={true} />
            </PrivateRoute>

            <PrivateRoute auth={isLogin} hasPermissions={true} exact path="/editar/licencia/:id">
              <NewItem edit={true}/>
            </PrivateRoute>

            <PrivateRoute auth={isLogin} hasPermissions={true} exact path="/editar/dominio/:id">
              <NewItem edit={true}/>
            </PrivateRoute>

            <PrivateRoute auth={isLogin} hasPermissions={true} exact path="/editar/certificado/:id">
              <NewItem edit={true}/>
            </PrivateRoute>

            <PrivateRoute auth={isLogin} hasPermissions={true} exact path="/editar/partner/:id">
              <NewItem edit={true}/>
            </PrivateRoute>



            <PrivateRoute
              auth={isLogin}
              hasPermissions={true}
              exact
              path="/partner"
            >
              <Partner></Partner>
            </PrivateRoute>

            {/* Sin gestionar_usuarios redirige a "/"; el back responde 403 igualmente */}
            <PrivateRoute auth={isLogin} hasPermissions={canManageUsers} exact path="/usuarios">
              <Users />
            </PrivateRoute>


          </Switch>
        </div>
      </div>
    </>
  );
};
const PrivateRoute = (props: any) => {
  if (props.auth) {
    if (props.hasPermissions) {
      return <Route {...props}></Route>;
    } else {
      return <Redirect to="/" />;
    }
  } else {
    return <Redirect to="/login" />;
  }
};
